import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import {
  useSectionCompletionStore,
  sectionKey,
} from "@/stores/sectionCompletionStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { SectionContent } from "@/components/course/SectionContent";
import { MODULE_OUTRO_FALLBACK } from "@/components/course/introFallbacks";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";

interface ModuleOutroPageProps {
  module: CourseTreeNode;
}

export const ModuleOutroPage = ({ module }: ModuleOutroPageProps) => {
  const navigate = useNavigate();
  const basePath = useCourseBasePath();
  const { enrollmentId } = useParams<{ enrollmentId?: string }>();
  const { courseData } = useCourseDetailStore();
  const markCompleted = useSectionCompletionStore((s) => s.markCompleted);

  const { moduleLabel, nextTarget, ctaLabel } = useMemo(() => {
    const modules =
      courseData?.structure?.filter(
        (n) => n.type === CourseTreeNodeTypeEnum.Module
      ) ?? [];
    const idx = modules.findIndex((m) => m.id === module.id);
    const label =
      idx >= 0 && modules.length > 0
        ? `Modul ${idx + 1} von ${modules.length}`
        : undefined;

    if (idx >= 0 && idx < modules.length - 1) {
      return {
        moduleLabel: label,
        nextTarget: modules[idx + 1],
        ctaLabel: "Nächstes Modul",
      };
    }

    return {
      moduleLabel: label,
      nextTarget: null,
      ctaLabel: "Kurs abschließen",
    };
  }, [module.id, courseData]);

  const handleNext = () => {
    markCompleted(sectionKey("outro", module.id));
    if (nextTarget) {
      navigate(`${basePath}/${nextTarget.id}`);
    } else if (enrollmentId) {
      navigate(`/course/${enrollmentId}/dashboard`);
    } else {
      navigate("/");
    }
  };

  return (
    <SectionContent
      badge={moduleLabel}
      title={module.title}
      body={module.outro || MODULE_OUTRO_FALLBACK}
      ctaLabel={ctaLabel}
      onStart={handleNext}
    />
  );
};
